import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createCampaign } from '../../../services/Http/Api';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MdCampaign } from 'react-icons/md';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import { Section, Grid } from '../../../shared/layout/containers/Section';
import {
    StyledInput,
    StyledLabel,
    StyledTextArea,
    FormGroup,
    FormFooter
} from '../../../shared/layout/forms/FormElements';
import { toast } from 'react-toastify';

const CampaignCreate = () => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [budget, setBudget] = useState('');
    const [goalDescription, setGoalDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const campaignData = {
                name,
                description,
                start_date: startDate,
                end_date: endDate,
                budget,
                goal_description: goalDescription,
            };

            await createCampaign(campaignData);
            navigate('/campaigns');
        } catch (error) {
            console.error('Error creating campaign:', error);
            toast.error(t('Campaigns.CampaignCreate.error', { defaultValue: 'Failed to create campaign.' }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle icon={<MdCampaign size={28} />}>{t('Campaigns.CampaignCreate.title')}</PageTitle>
            </PageHeader>
            <PageContent>
                <StyledCard>
                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <StyledLabel htmlFor="name">{t('Campaigns.CampaignCreate.name')}</StyledLabel>
                            <StyledInput
                                type="text"
                                id="name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <StyledLabel htmlFor="description">{t('Campaigns.CampaignCreate.description')}</StyledLabel>
                            <StyledTextArea
                                id="description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </FormGroup>
                        <Grid columns={2}>
                            <FormGroup>
                                <StyledLabel htmlFor="startDate">{t('Campaigns.CampaignCreate.startDate')}</StyledLabel>
                                <StyledInput
                                    type="date"
                                    id="startDate"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    required
                                />
                            </FormGroup>
                            <FormGroup>
                                <StyledLabel htmlFor="endDate">{t('Campaigns.CampaignCreate.endDate')}</StyledLabel>
                                <StyledInput
                                    type="date"
                                    id="endDate"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    required
                                />
                            </FormGroup>
                        </Grid>
                        <FormGroup>
                            <StyledLabel htmlFor="budget">{t('Campaigns.CampaignCreate.budget')}</StyledLabel>
                            <StyledInput
                                type="number"
                                id="budget"
                                value={budget}
                                onChange={(e) => setBudget(e.target.value)}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <StyledLabel htmlFor="goalDescription">{t('Campaigns.CampaignCreate.goalDescription')}</StyledLabel>
                            <StyledTextArea
                                id="goalDescription"
                                value={goalDescription}
                                onChange={(e) => setGoalDescription(e.target.value)}
                            />
                        </FormGroup>
                        <FormFooter>
                            <MainButton variant="secondary" onClick={() => navigate('/campaigns')}>
                                {t('Campaigns.CampaignCreate.cancel')}
                            </MainButton>
                            <MainButton type="submit" disabled={loading}>
                                {loading ? t('Campaigns.CampaignCreate.creating') : t('Campaigns.CampaignCreate.createCampaign')}
                            </MainButton>
                        </FormFooter>
                    </Form>
                </StyledCard>
            </PageContent>
        </PageContainer>
    );
};

export default CampaignCreate;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    max-width: 800px;
`;
